// src/pages/TrialPage.tsx
import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, ArrowRight, CheckCircle, Users, CreditCard } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'

export default function TrialPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)

  const features = [
    'Track your trading discipline',
    'Build green discipline momentum',
    'Easy, clean, intuitive interface', 
    'Focus on essential metrics',
    'Start journaling in seconds',
    'Journal without pain',
    'Access to all premium features for 3 days',
    'AI Trading Coach included in trial',
    'Cancel anytime during trial period',
  ]

  const handleTrialSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return

    setLoading(true)
    try {
      const res = await fetch('/.netlify/functions/create-trial-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      if (!res.ok) throw new Error(await res.text() || res.statusText)
      const { redirectUrl } = await res.json()

      // Force a re-check of the subscription status on the next page load
      localStorage.removeItem('lastSubscriptionVerified')
      
      window.location.href = redirectUrl
    } catch (err: any) {
      console.error('Trial signup error:', err)
      alert('Failed to start trial. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div data-page="trial" className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="container mx-auto max-w-2xl mb-12 flex-grow">
        <div className="mb-8">
          <Button
            as={Link}
            to="/"
            variant="ghost"
            className="flex items-center"
          >
            <ArrowLeft size={18} className="mr-1" />
            Back to Home
          </Button>
        </div>

        <Card>
          <CardContent className="px-6 md:px-8 py-8">

          {/* Hero */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-4 text-gray-950">
              Start Your <span className="gradient-text">$0 Trial</span>
            </h1>
            
            {/* No Card Needed - Prominent Display */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <CreditCard size={18} className="text-black dark:text-white" />
              <p className="text-lg font-bold text-black dark:text-white">
                NO CARD NEEDED
              </p>
            </div>
            
            <div className="flex items-center justify-center gap-2 mb-4">
              <Users size={18} className="text-black dark:text-white" />
              <p className="text-lg text-black dark:text-white font-bold">
                Join 100+ disciplined traders who started this month
              </p>
            </div>
            
            {/* Value Proposition */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
              <p className="text-emerald-800 font-bold text-lg">
                Get full access to all premium features
              </p>
              <p className="text-emerald-700 text-sm">
                3-day trial • No restrictions • All features included
              </p>
            </div>
          </div>

          {/* Feature list */}
          <div className="space-y-3 mb-8">
            {features.map((feature, idx) => (
              <div key={idx} className="flex items-start">
                <CheckCircle
                  size={18}
                  className="mr-3 mt-0.5 flex-shrink-0 text-emerald-500"
                />
                <p className={`text-gray-700 ${
                  feature.includes('AI Trading Coach') || feature.includes('premium features') 
                    ? 'font-bold' 
                    : ''
                }`}>
                  {feature}
                </p>
              </div>
            ))}
          </div>

          {/* Social Proof */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-blue-800 text-sm font-medium text-center">
              ⚡ Most trial users upgrade to paid plans
            </p>
          </div>

          {/* Signup form */}
          <form onSubmit={handleTrialSignup} className="space-y-4">
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Enter your email to start your trial"
              className="w-full p-4 border border-gray-300/50 rounded-lg bg-white/50 backdrop-blur-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 transition-all duration-300 text-lg"
              required
            />
            <button
              type="submit"
              disabled={!email || loading}
              className={`w-full !rounded-full py-5 text-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col items-center justify-center ${
                !email || loading
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-black hover:bg-gray-900 text-white'
              }`}
            >
              <div className="flex items-center">
                {loading ? 'Starting Your Trial...' : 'Start $0 Trial'}
                <ArrowRight size={24} className="ml-3" />
              </div>
              <span className="text-xs font-normal mt-1">No card needed</span>
            </button>
          </form>

          {/* Risk Reversal */}
          <div className="text-center mt-6">
            <p className="text-sm text-gray-500 mb-4">
              ✅ No card needed • ✅ Cancel anytime • ✅ No hidden fees • ✅ Instant access
            </p>
            <p className="text-lg text-gray-700">
              <Link to="/pricing" className="text-primary-700 hover:underline font-medium">
                Want to skip the trial? View pricing plans →
              </Link>
            </p>
          </div>
          </CardContent>
        </Card>
      </div>

    </div>
  )
}